import {
  Activity,
  BookOpen,
  BriefcaseBusiness,
  CalendarCheck,
  ChartPie,
  ClipboardCheck,
  Code,
  Database,
  FileText,
  Funnel,
  Globe,
  LifeBuoy,
  MailCheck,
  Megaphone,
  MousePointerClick,
  PenLine,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  Telescope,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/** Colours of a desk, as on the landing page: every desk gets its own (stable per desk). */
export interface DeskTone {
  /** Icon tile. */
  tile: string;
  /** Sparkline and accents. */
  text: string;
  /** Soft wash behind the card. */
  wash: string;
  ring: string;
}

const TONES: DeskTone[] = [
  {
    tile: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    text: 'text-violet-600 dark:text-violet-400',
    wash: 'from-violet-500/[0.07]',
    ring: 'group-hover:border-violet-300 dark:group-hover:border-violet-800',
  },
  {
    tile: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    text: 'text-sky-600 dark:text-sky-400',
    wash: 'from-sky-500/[0.07]',
    ring: 'group-hover:border-sky-300 dark:group-hover:border-sky-800',
  },
  {
    tile: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    text: 'text-emerald-600 dark:text-emerald-400',
    wash: 'from-emerald-500/[0.07]',
    ring: 'group-hover:border-emerald-300 dark:group-hover:border-emerald-800',
  },
  {
    tile: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    text: 'text-rose-600 dark:text-rose-400',
    wash: 'from-rose-500/[0.07]',
    ring: 'group-hover:border-rose-300 dark:group-hover:border-rose-800',
  },
  {
    tile: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    text: 'text-indigo-600 dark:text-indigo-400',
    wash: 'from-indigo-500/[0.07]',
    ring: 'group-hover:border-indigo-300 dark:group-hover:border-indigo-800',
  },
  {
    tile: 'bg-teal-500/10 text-teal-600 dark:text-teal-400',
    text: 'text-teal-600 dark:text-teal-400',
    wash: 'from-teal-500/[0.07]',
    ring: 'group-hover:border-teal-300 dark:group-hover:border-teal-800',
  },
  {
    tile: 'bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400',
    text: 'text-fuchsia-600 dark:text-fuchsia-400',
    wash: 'from-fuchsia-500/[0.07]',
    ring: 'group-hover:border-fuchsia-300 dark:group-hover:border-fuchsia-800',
  },
  {
    tile: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    text: 'text-orange-600 dark:text-orange-400',
    wash: 'from-orange-500/[0.07]',
    ring: 'group-hover:border-orange-300 dark:group-hover:border-orange-800',
  },
];

function hash(seed: string) {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function deskTone(deskId: string): DeskTone {
  return TONES[hash(deskId) % TONES.length]!;
}

/** First match wins: the desk's name, then its assignment. */
const ICONS: [RegExp, LucideIcon][] = [
  [/\bseo\b|search console|keyword/, Search],
  [/\bsea\b|google ads|\bppc\b|adwords/, MousePointerClick],
  [/\bmeta\b|facebook|instagram|\bads?\b|campaign/, Target],
  [/linkedin/, BriefcaseBusiness],
  [/analytic|audience|report|\bkpi/, ChartPie],
  [/website|landing|\bweb\b|\bsite\b/, Globe],
  [/securit|vulnerab|\bcve|pentest/, ShieldCheck],
  [/lead|qualif|prospect/, Funnel],
  [/sales|pipeline|forecast|monitor/, Activity],
  [/follow.?up|e-?mail|\bmail|inbox|newsletter/, MailCheck],
  [/meeting|calendar|\bcall/, CalendarCheck],
  [/proposal|offer|contract|quote/, FileText],
  [/support|\bcases?\b|ticket|customer|helpdesk/, LifeBuoy],
  [/engineer|\bcode|\bbugs?\b|release|deploy|\bgit|software|\bdev/, Code],
  [/research|competitor|market/, Telescope],
  [/content|\bblog|copy|social|\bposts?\b|writ/, PenLine],
  [/marketing|\bpr\b|brand/, Megaphone],
  [/financ|account|invoice|\bbank|payment|billing/, Wallet],
  [/\bhr\b|recruit|hiring|onboard|people/, Users],
  [/\bdata\b|database|\bsql/, Database],
  [/\bdocs?\b|documentation|wiki|knowledge/, BookOpen],
  [/\bops\b|daily|operations|check/, ClipboardCheck],
];

export function deskIcon(desk: { name: string; assignment: string }): LucideIcon {
  for (const text of [desk.name.toLowerCase(), desk.assignment.toLowerCase()]) {
    for (const [re, icon] of ICONS) if (re.test(text)) return icon;
  }
  return Sparkles;
}
